"use client";

import { theme } from "@/config/theme";
import { SCButton, SCContainerContent } from "@/utils/styledComponents";
import { useTranslations } from "next-intl";
import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import { useRouter, useParams } from "next/navigation";
import { useRecoilValue } from "recoil";
import { appLocale } from "@/recoil/appState";
import { trackBookTrialPageView } from "@/utils/metaTracking";
import { trackFormStart, trackFormStep } from "@/utils/ga4";
import { sessionPrivate } from "@/utils/sessionPrivacy";
import CalendarPicker from "./CalendarPicker";
import {
    deriveAvailableDays,
    buildSlotsUrl,
    buildPoolsUrl,
    fallbackPoolsFor,
    groupPoolsByRegion,
    type PoolChoice
} from "./bookingHelpers";
import toast, { Toaster } from "react-hot-toast";
import { canonicalPath } from "@/utils/canonicalPath";

// Shared by every form_start / form_step event on this page, so the funnel
// groups on one value rather than on the page path.
const FORM_NAME = "book_trial";

// ------------------------------------------
// TYPES
// ------------------------------------------
interface ChildEntry {
    id: number;
    name: string;
    age: number | null;
}

// ------------------------------------------
// COMPONENT
// ------------------------------------------
const BookTrial = () => {
    const t = useTranslations("BookTrial");
    const router = useRouter();
    const params = useParams();
    const locale = params.locale as string;
    const localeAppState = useRecoilValue(appLocale);

    useEffect(() => {
        trackBookTrialPageView();
    }, []);

    const [today, setToday] = useState("");
    useEffect(() => {
        setToday(new Date().toISOString().split("T")[0]);
    }, []);

    const [countryCode, setCountryCode] = useState("+65");
    useEffect(() => {
        setCountryCode(locale === "id" ? "+62" : "+65");
    }, [locale]);

    // Step state
    const [selectedState, setSelectedState] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedSlot, setSelectedSlot] = useState("");
    const [step2Locked, setStep2Locked] = useState(true);
    const [step3Locked, setStep3Locked] = useState(true);
    const [slotSummaryVisible, setSlotSummaryVisible] = useState(false);
    const [formVisible, setFormVisible] = useState(false);

    // Live slots for the selected pool: { "YYYY-MM-DD": ["9:45 AM", ...] }
    const [slotsByDate, setSlotsByDate] = useState<Record<string, string[]>>(
        {}
    );
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slotsError, setSlotsError] = useState(false);

    // Bookable pools for the current country/province, from Airtable.
    const [pools, setPools] = useState<PoolChoice[]>([]);
    const [poolsLoading, setPoolsLoading] = useState(false);

    // A pool is offered exactly when Airtable says it has trial slots. On
    // failure we fall back to a static list rather than render an empty
    // dropdown — a booking form nobody can use converts nobody.
    useEffect(() => {
        if (locale === "id" && !selectedState) {
            setPools([]);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setPoolsLoading(true);
            try {
                const res = await fetch(
                    buildPoolsUrl({
                        country: locale,
                        province: selectedState
                    }),
                    { cache: "no-store" }
                );
                if (!res.ok) throw new Error("Failed to load pools");
                const data = await res.json();
                if (cancelled) return;
                const live: PoolChoice[] = data.pools ?? [];
                setPools(
                    live.length ? live : fallbackPoolsFor(locale, selectedState)
                );
            } catch {
                if (!cancelled) {
                    setPools(fallbackPoolsFor(locale, selectedState));
                }
            } finally {
                if (!cancelled) setPoolsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [locale, selectedState]);

    const loadSlots = async (location: string) => {
        setSlotsLoading(true);
        setSlotsError(false);
        try {
            const res = await fetch(
                buildSlotsUrl({
                    country: locale,
                    province: selectedState,
                    pool: location
                }),
                { cache: "no-store" }
            );
            if (!res.ok) throw new Error("Failed to load slots");
            const data = await res.json();
            setSlotsByDate(data.slotsByDate ?? {});
        } catch {
            setSlotsError(true);
            setSlotsByDate({});
        } finally {
            setSlotsLoading(false);
        }
    };

    // Children
    const [nextChildId, setNextChildId] = useState(2);
    const [children, setChildren] = useState<ChildEntry[]>([
        { id: 1, name: "", age: null }
    ]);

    // Contact
    const [parentPhone, setParentPhone] = useState("");
    const [parentEmail, setParentEmail] = useState("");
    const [parentName, setParentName] = useState("");

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState<
        Record<string, string>
    >({});
    const [error, setError] = useState<string | null>(null);

    // ------------------------------------------
    // HELPERS
    // ------------------------------------------
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr + "T00:00:00");
        return date.toLocaleDateString("en-SG", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric"
        });
    };

    const getAvailableDays = (): string[] => {
        return deriveAvailableDays(Object.keys(slotsByDate));
    };

    const getAvailableSlots = (): string[] => {
        if (!selectedDate) return [];
        return slotsByDate[selectedDate] ?? [];
    };

    // Label comes from the same list that populated the dropdown, so a pool
    // added in Airtable reads correctly in the summary too. Falls back to
    // title-casing the slug if the pool list is not loaded yet.
    const getLocationLabel = (): string => {
        if (!selectedLocation) return "";
        const known = pools.find((p) => p.slug === selectedLocation);
        if (known) return known.label;
        return selectedLocation
            .split("-")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    };

    // ------------------------------------------
    // STEP HANDLERS
    // ------------------------------------------

    // TD-129 needs to know where the 137 form_start → 3 form_submit drop-off
    // happens. Diagnostics only — these are not key events (TD-146).
    const formStarted = useRef(false);

    const trackStep = (step: string) => {
        if (!formStarted.current) {
            formStarted.current = true;
            trackFormStart(FORM_NAME);
        }
        trackFormStep(FORM_NAME, step);
    };

    const handleLocationChange = (location: string) => {
        setSelectedLocation(location);
        setSelectedDate("");
        setSelectedSlot("");
        setStep3Locked(true);
        setSlotSummaryVisible(false);
        setFormVisible(false);
        setStep2Locked(!location);
        // Clearing the field is a reset, not progress through the funnel.
        if (location) trackStep("location");
        if (location) {
            loadSlots(location);
        } else {
            setSlotsByDate({});
            setSlotsError(false);
        }
    };

    const handleDateSelect = (date: string) => {
        setSelectedDate(date);
        setSelectedSlot("");
        setSlotSummaryVisible(false);
        setFormVisible(false);
        setStep3Locked(!date);
        if (date) trackStep("date");
    };

    const handleSlotChange = (slot: string) => {
        setSelectedSlot(slot);
        setSlotSummaryVisible(!!slot);
        setFormVisible(!!slot);
        // Reaching a slot is what reveals the lead fields, so this is the last
        // step before the drop-off TD-129 is trying to locate.
        if (slot) trackStep("time");
    };

    // ------------------------------------------
    // CHILDREN HANDLERS
    // ------------------------------------------
    const addChild = () => {
        setChildren((prev) => [
            ...prev,
            { id: nextChildId, name: "", age: null }
        ]);
        setNextChildId((prev) => prev + 1);
    };

    const removeChild = (id: number) => {
        setChildren((prev) => prev.filter((c) => c.id !== id));
    };

    const updateChildName = (id: number, name: string) => {
        setChildren((prev) =>
            prev.map((c) => (c.id === id ? { ...c, name } : c))
        );
    };

    const updateChildAge = (id: number, age: number | null) => {
        setChildren((prev) =>
            prev.map((c) => (c.id === id ? { ...c, age } : c))
        );
    };

    // ------------------------------------------
    // VALIDATION
    // ------------------------------------------
    const validate = (): boolean => {
        const errors: Record<string, string> = {};

        if (!selectedLocation) errors.location = "Please select a location.";
        if (!selectedDate) errors.date = "Please select a date.";
        if (!selectedSlot) errors.slot = "Please select a time slot.";

        children.forEach((child, index) => {
            if (!child.age || child.age < 4)
                errors[`childAge${index}`] =
                    "Please enter a valid age (min 4).";
            if (!child.name.trim())
                errors[`childName${index}`] = "Please enter the child's name.";
        });

        if (!parentPhone.trim()) {
            errors.parentPhone = "Please enter a phone number.";
        } else if (locale === "sg" && !/^\d{8}$/.test(parentPhone.trim())) {
            errors.parentPhone =
                "Please enter a valid 8-digit Singapore number.";
        } else if (!/^\d+$/.test(parentPhone.trim())) {
            errors.parentPhone =
                "Please enter a valid phone number (digits only).";
        }

        if (!parentEmail.trim()) {
            errors.parentEmail = "Please enter your email address.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail.trim())) {
            errors.parentEmail = "Please enter a valid email address.";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // ------------------------------------------
    // SUBMIT
    // ------------------------------------------
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) {
            setError("Please fix the errors above.");
            // A failed validation is its own drop-off cause, and one worth
            // separating from people who simply never reached submit (TD-129).
            trackStep("validation_failed");
            return;
        }
        trackStep("details");

        setIsSubmitting(true);
        setError(null);

        try {
            const dateObj = new Date(selectedDate + "T00:00:00");
            const dayOfWeek = dateObj.toLocaleDateString("en-US", {
                weekday: "long"
            });
            const fullPhoneNumber = parseInt(
                countryCode.replace("+", "") + parentPhone.trim()
            );
            const agesArray = children.map((c) => c.age ?? 5);
            const namesArray = children.map((c) => c.name.trim());

            const bookingData = {
                pool: selectedLocation,
                timeslot: selectedSlot,
                date: selectedDate,
                day: dayOfWeek,
                parent_name: parentName.trim() || "",
                parent_email: parentEmail.trim(),
                phone_number: fullPhoneNumber,
                number_of_children: children.length,
                children_name: namesArray.join(", "),
                age: agesArray,
                country: locale === "sg" ? "sg" : "id",
                state: locale === "sg" ? "sg" : selectedState || null
            };

            const response = await fetch("/api/booking", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(bookingData)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || "Booking submission failed");
            }

            const confirmationData = {
                pool: selectedLocation,
                date: selectedDate,
                timeslot: selectedSlot,
                name: parentName.trim(),
                phone: `${countryCode} ${parentPhone.trim()}`,
                children: children.length,
                ageGroups: children.map((c) => c.age?.toString() ?? ""),
                childNames: namesArray
            };
            sessionStorage.setItem(
                "bookingConfirmation",
                JSON.stringify(confirmationData)
            );
            router.push(
                canonicalPath(`/${localeAppState}/booking-confirmation`)
            );
        } catch (err) {
            console.error("Error submitting booking:", err);
            const msg =
                err instanceof Error
                    ? err.message
                    : "There was an error submitting your booking. Please try again.";
            setError(msg);
            toast.error(msg, { duration: 6000 });
            setIsSubmitting(false);
        }
    };

    const availableDays = getAvailableDays();
    const availableSlots = getAvailableSlots();

    // ------------------------------------------
    // RENDER
    // ------------------------------------------
    return (
        <SCBookTrialPage>
            <Toaster position="top-center" />
            <SCBanner>
                <SCBannerContent>
                    <h1>{t("banner-title")}</h1>
                    <p className="banner-subtitle">{t("banner-subtitle")}</p>
                </SCBannerContent>
            </SCBanner>

            <SCBookingSection>
                <SCBookingContainer>
                    <SCBookingCard>
                        {/* Step 1: Location */}
                        <SCBkStep>
                            <div className="bk-step-label">
                                <span className="bk-step-num">1</span>
                                {t("choose-location")}
                            </div>

                            {locale === "id" && (
                                <SCBkSelect
                                    value={selectedState}
                                    onChange={(e) => {
                                        setSelectedState(e.target.value);
                                        handleLocationChange("");
                                    }}
                                >
                                    <option value="">Select province...</option>
                                    <option value="jakarta">Jakarta</option>
                                    <option value="bali">Bali</option>
                                    <option value="medan">Medan</option>
                                </SCBkSelect>
                            )}

                            <SCBkSelect
                                value={selectedLocation}
                                onChange={(e) =>
                                    handleLocationChange(e.target.value)
                                }
                                disabled={locale === "id" && !selectedState}
                                $hasError={!!validationErrors.location}
                            >
                                <option value="">
                                    {locale === "id" && !selectedState
                                        ? "Please select province first"
                                        : poolsLoading
                                          ? "Loading locations..."
                                          : t("select-location-placeholder")}
                                </option>
                                {/* Options come from Airtable via /api/pools,
                                    so a pool is bookable exactly when it has
                                    trial slots. groupPoolsByRegion returns one
                                    unnamed group for ID's flat list. */}
                                {groupPoolsByRegion(pools).map((group) =>
                                    group.region ? (
                                        <optgroup
                                            key={group.region}
                                            label={group.region}
                                        >
                                            {group.pools.map((pool) => (
                                                <option
                                                    key={pool.slug}
                                                    value={pool.slug}
                                                >
                                                    {pool.label}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ) : (
                                        group.pools.map((pool) => (
                                            <option
                                                key={pool.slug}
                                                value={pool.slug}
                                            >
                                                {pool.label}
                                            </option>
                                        ))
                                    )
                                )}
                            </SCBkSelect>
                            {validationErrors.location && (
                                <SCFieldError>
                                    {validationErrors.location}
                                </SCFieldError>
                            )}
                        </SCBkStep>

                        {/* Step 2: Date */}
                        <SCBkStep $locked={step2Locked}>
                            <div className="bk-step-label">
                                <span className="bk-step-num">2</span>
                                {t("choose-date")}
                            </div>
                            {!step2Locked && slotsLoading && (
                                <SCHelperText>
                                    Loading available dates…
                                </SCHelperText>
                            )}
                            {!step2Locked && slotsError && (
                                <SCFieldError>
                                    Couldn&apos;t load available slots.{" "}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            loadSlots(selectedLocation)
                                        }
                                        style={{
                                            background: "none",
                                            border: "none",
                                            color: theme["color-blue"],
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            padding: 0,
                                            font: "inherit"
                                        }}
                                    >
                                        Retry
                                    </button>
                                </SCFieldError>
                            )}
                            {!step2Locked &&
                                !slotsLoading &&
                                !slotsError &&
                                availableDays.length > 0 && (
                                    <SCHelperText>
                                        &#128197; {t("available-on")}{" "}
                                        <strong>
                                            {availableDays.join(", ")}
                                        </strong>
                                    </SCHelperText>
                                )}
                            <CalendarPicker
                                availableDays={availableDays}
                                selectedDate={selectedDate}
                                onDateSelect={handleDateSelect}
                                disabled={step2Locked}
                                todayStr={today}
                            />
                            {validationErrors.date && (
                                <SCFieldError>
                                    {validationErrors.date}
                                </SCFieldError>
                            )}
                        </SCBkStep>

                        {/* Step 3: Time Slot */}
                        <SCBkStep $locked={step3Locked}>
                            <div className="bk-step-label">
                                <span className="bk-step-num">3</span>
                                {t("available-slots")}
                            </div>
                            <SCBkSelect
                                value={selectedSlot}
                                onChange={(e) =>
                                    handleSlotChange(e.target.value)
                                }
                                disabled={step3Locked}
                                $hasError={!!validationErrors.slot}
                            >
                                <option value="">
                                    {!selectedDate
                                        ? "Select date first..."
                                        : availableSlots.length === 0
                                          ? "No slots available for this day"
                                          : t("select-time-placeholder")}
                                </option>
                                {availableSlots.map((time) => (
                                    <option key={time} value={time}>
                                        {time}
                                    </option>
                                ))}
                            </SCBkSelect>
                            {validationErrors.slot && (
                                <SCFieldError>
                                    {validationErrors.slot}
                                </SCFieldError>
                            )}
                        </SCBkStep>

                        {/* Slot Summary */}
                        {slotSummaryVisible && (
                            <SCSlotSummary>
                                <div className="slot-summary-title">
                                    &#127881; {t("your-slot-is")}
                                </div>
                                <div className="slot-summary-details">
                                    <span>&#128205; {getLocationLabel()}</span>
                                    <span>
                                        &#128197; {formatDate(selectedDate)},{" "}
                                        {selectedSlot}
                                    </span>
                                </div>
                            </SCSlotSummary>
                        )}

                        {/* Booking Form */}
                        {formVisible && (
                            <SCBkFormSection>
                                {/* TD-148: children's names and ages, parent
                                    name, phone and email. Masked from the
                                    session recorders; interactions still
                                    record, which is what TD-129 needs. */}
                                <form
                                    onSubmit={handleSubmit}
                                    noValidate
                                    {...sessionPrivate()}
                                >
                                    {/* Children */}
                                    <div>
                                        {children.map((child, index) => (
                                            <SCChildEntry key={child.id}>
                                                <div className="child-entry-label">
                                                    {index === 0
                                                        ? t("childs-age")
                                                        : `${t("child")} ${index + 1} ${t("age")}`}
                                                </div>
                                                <SCChildAgeInput
                                                    type="number"
                                                    min={4}
                                                    max={18}
                                                    placeholder="e.g. 7"
                                                    value={child.age ?? ""}
                                                    $hasError={
                                                        !!validationErrors[
                                                            `childAge${index}`
                                                        ]
                                                    }
                                                    onChange={(e) => {
                                                        const val =
                                                            e.target.value;
                                                        updateChildAge(
                                                            child.id,
                                                            val
                                                                ? parseInt(val)
                                                                : null
                                                        );
                                                        if (
                                                            validationErrors[
                                                                `childAge${index}`
                                                            ]
                                                        ) {
                                                            const errs = {
                                                                ...validationErrors
                                                            };
                                                            delete errs[
                                                                `childAge${index}`
                                                            ];
                                                            setValidationErrors(
                                                                errs
                                                            );
                                                        }
                                                    }}
                                                />
                                                {validationErrors[
                                                    `childAge${index}`
                                                ] && (
                                                    <SCFieldError>
                                                        {
                                                            validationErrors[
                                                                `childAge${index}`
                                                            ]
                                                        }
                                                    </SCFieldError>
                                                )}
                                                <SCChildNameInput
                                                    type="text"
                                                    placeholder={
                                                        index === 0
                                                            ? t(
                                                                  "child-name-placeholder"
                                                              )
                                                            : `${t("child")} ${index + 1} ${t("name")}`
                                                    }
                                                    value={child.name}
                                                    $hasError={
                                                        !!validationErrors[
                                                            `childName${index}`
                                                        ]
                                                    }
                                                    onChange={(e) => {
                                                        updateChildName(
                                                            child.id,
                                                            e.target.value
                                                        );
                                                        if (
                                                            validationErrors[
                                                                `childName${index}`
                                                            ]
                                                        ) {
                                                            const errs = {
                                                                ...validationErrors
                                                            };
                                                            delete errs[
                                                                `childName${index}`
                                                            ];
                                                            setValidationErrors(
                                                                errs
                                                            );
                                                        }
                                                    }}
                                                />
                                                {validationErrors[
                                                    `childName${index}`
                                                ] && (
                                                    <SCFieldError>
                                                        {
                                                            validationErrors[
                                                                `childName${index}`
                                                            ]
                                                        }
                                                    </SCFieldError>
                                                )}
                                                {index > 0 && (
                                                    <SCChildRemoveBtn
                                                        type="button"
                                                        onClick={() =>
                                                            removeChild(
                                                                child.id
                                                            )
                                                        }
                                                        title="Remove"
                                                    >
                                                        &#10005;
                                                    </SCChildRemoveBtn>
                                                )}
                                            </SCChildEntry>
                                        ))}
                                    </div>

                                    <SCAddChildBtn
                                        type="button"
                                        onClick={addChild}
                                    >
                                        &#10133; {t("add-another-child")}
                                    </SCAddChildBtn>

                                    {/* Parent Details */}
                                    <SCBkFormDivider />
                                    <SCConfirmLabel>
                                        {t("confirm-booking")}{" "}
                                        <span>({t("takes-30-sec")})</span>
                                    </SCConfirmLabel>

                                    <SCFormGroup>
                                        <label>
                                            {t("phone-number")}{" "}
                                            <span className="required">*</span>
                                        </label>
                                        <SCPhoneInputWrap
                                            $hasError={
                                                !!validationErrors.parentPhone
                                            }
                                        >
                                            {locale === "sg" ? (
                                                <span className="phone-prefix">
                                                    +65
                                                </span>
                                            ) : (
                                                <select
                                                    value={countryCode}
                                                    onChange={(e) =>
                                                        setCountryCode(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="+62">
                                                        +62
                                                    </option>
                                                    <option value="+65">
                                                        +65
                                                    </option>
                                                    <option value="+60">
                                                        +60
                                                    </option>
                                                </select>
                                            )}
                                            <input
                                                type="tel"
                                                placeholder={
                                                    locale === "sg"
                                                        ? "9123 4567"
                                                        : "87654321"
                                                }
                                                maxLength={
                                                    locale === "sg"
                                                        ? 8
                                                        : undefined
                                                }
                                                value={parentPhone}
                                                onChange={(e) => {
                                                    setParentPhone(
                                                        e.target.value
                                                    );
                                                    if (
                                                        validationErrors.parentPhone
                                                    ) {
                                                        const errs = {
                                                            ...validationErrors
                                                        };
                                                        delete errs.parentPhone;
                                                        setValidationErrors(
                                                            errs
                                                        );
                                                    }
                                                }}
                                            />
                                        </SCPhoneInputWrap>
                                        {validationErrors.parentPhone && (
                                            <SCFieldError>
                                                {validationErrors.parentPhone}
                                            </SCFieldError>
                                        )}
                                    </SCFormGroup>

                                    <SCFormGroup>
                                        <label>{t("parent-email")}</label>
                                        <input
                                            type="email"
                                            placeholder={t(
                                                "parent-email-placeholder"
                                            )}
                                            value={parentEmail}
                                            aria-invalid={
                                                !!validationErrors.parentEmail
                                            }
                                            onChange={(e) => {
                                                setParentEmail(e.target.value);
                                                if (
                                                    validationErrors.parentEmail
                                                ) {
                                                    const errs = {
                                                        ...validationErrors
                                                    };
                                                    delete errs.parentEmail;
                                                    setValidationErrors(errs);
                                                }
                                            }}
                                        />
                                        {validationErrors.parentEmail && (
                                            <SCFieldError>
                                                {validationErrors.parentEmail}
                                            </SCFieldError>
                                        )}
                                    </SCFormGroup>

                                    <SCFormGroup>
                                        <label>
                                            {t("parent-name")}{" "}
                                            <span className="optional">
                                                ({t("optional")})
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder={t(
                                                "parent-name-placeholder"
                                            )}
                                            value={parentName}
                                            onChange={(e) =>
                                                setParentName(e.target.value)
                                            }
                                        />
                                    </SCFormGroup>

                                    {error && (
                                        <SCErrorMessage>{error}</SCErrorMessage>
                                    )}

                                    <SCSubmitButton
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting
                                            ? t("submitting-button")
                                            : t("submit-button")}
                                    </SCSubmitButton>

                                    <SCFormFooter>
                                        {t("no-payment-note")}
                                    </SCFormFooter>
                                </form>
                            </SCBkFormSection>
                        )}
                    </SCBookingCard>

                    <SCStatsSection>
                        <SCStatItem>
                            <div className="stat-value">100%</div>
                            <div className="stat-label">
                                {t("stat-satisfaction")}
                            </div>
                        </SCStatItem>
                        <SCStatItem>
                            <div className="stat-value">38+ Years</div>
                            <div className="stat-label">
                                {t("stat-experience")}
                            </div>
                        </SCStatItem>
                        <SCStatItem>
                            <div className="stat-value">Expert</div>
                            <div className="stat-label">
                                {t("stat-coaches")}
                            </div>
                        </SCStatItem>
                    </SCStatsSection>
                </SCBookingContainer>
            </SCBookingSection>
        </SCBookTrialPage>
    );
};

export default BookTrial;

// =============================================
// STYLED COMPONENTS
// =============================================

const SCBookTrialPage = styled.div`
    background-color: ${theme["color-white"]};
    min-height: 100vh;
`;

const SCBanner = styled.div`
    background: linear-gradient(135deg, #27b2ff 0%, #0075a6 100%);
    padding: 60px 20px 80px;
    margin-top: -80px;
    padding-top: 140px;
    text-align: center;
    color: ${theme["color-white"]};

    @media (max-width: 768px) {
        padding: 32px 16px 48px;
        padding-top: 90px;
        margin-top: -60px;
    }
`;

const SCBannerContent = styled(SCContainerContent)`
    max-width: 800px;
    margin: 0 auto;

    h1 {
        font-size: 42px;
        font-weight: bold;
        margin-bottom: 16px;
        font-family: "Brockmann", sans-serif;

        @media (max-width: 768px) {
            font-size: 26px;
            margin-bottom: 10px;
        }
    }

    .banner-subtitle {
        font-size: 18px;
        opacity: 0.95;

        @media (max-width: 768px) {
            font-size: 15px;
        }
    }
`;

const SCBookingSection = styled.div`
    padding: 60px 20px 80px;
    background-color: #f8fafc;

    @media (max-width: 768px) {
        padding: 32px 16px 48px;
    }
`;

const SCBookingContainer = styled(SCContainerContent)`
    max-width: 680px;
    margin: 0 auto;
`;

const SCBookingCard = styled.div`
    background: white;
    border-radius: 20px;
    box-shadow:
        0 16px 48px rgba(0, 0, 0, 0.1),
        0 8px 24px rgba(0, 0, 0, 0.06);
    border: 1px solid #f1f5f9;
    padding: 2.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-top: -60px;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
        padding: 1.5rem;
        margin-top: -32px;
        gap: 1.25rem;
    }
`;

const SCBkStep = styled.div<{ $locked?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    transition: opacity 0.2s;
    opacity: ${({ $locked }) => ($locked ? 0.4 : 1)};
    pointer-events: ${({ $locked }) => ($locked ? "none" : "auto")};

    .bk-step-label {
        font-size: 0.95rem;
        font-weight: 800;
        color: #0c1a2e;
        display: flex;
        align-items: center;
        gap: 0.6rem;
    }

    .bk-step-num {
        width: 26px;
        height: 26px;
        background: ${theme["color-blue"]};
        color: white;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: 900;
        flex-shrink: 0;
    }
`;

const SCBkSelect = styled.select<{ $hasError?: boolean }>`
    width: 100%;
    padding: 0.8rem 2.5rem 0.8rem 1rem;
    border: 1.5px solid
        ${({ $hasError }) => ($hasError ? "#ef4444" : "#e2e8f0")};
    border-radius: 12px;
    font-size: 0.95rem;
    color: #0f172a;
    background: white;
    outline: none;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.9rem center;
    transition:
        border-color 0.2s,
        box-shadow 0.2s;

    &:focus {
        border-color: ${theme["color-blue"]};
        box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
    }

    &:disabled {
        background-color: #f8fafc;
        cursor: default;
    }
`;

const SCSlotSummary = styled.div`
    background: linear-gradient(135deg, #ecfdf5, #f0fdf4);
    border: 1.5px solid #86efac;
    border-radius: 16px;
    padding: 1.25rem 1.5rem;

    .slot-summary-title {
        font-size: 1rem;
        font-weight: 800;
        color: #15803d;
        margin-bottom: 0.6rem;
    }

    .slot-summary-details {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
        font-size: 0.925rem;
        color: #334155;
        font-weight: 500;
    }
`;

const SCBkFormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const SCBkFormDivider = styled.div`
    height: 1px;
    background: #f1f5f9;
    margin: 0.5rem 0;
`;

const SCConfirmLabel = styled.div`
    font-size: 1rem;
    font-weight: 800;
    color: #0c1a2e;
    margin-bottom: 0.25rem;

    span {
        font-family: inherit;
        font-size: 0.85rem;
        font-weight: 400;
        color: #94a3b8;
    }
`;

const SCChildEntry = styled.div`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    position: relative;
    margin-bottom: 0.75rem;

    .child-entry-label {
        font-size: 0.8rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: #64748b;
    }
`;

const SCChildAgeInput = styled.input<{ $hasError?: boolean }>`
    width: 100%;
    padding: 0.7rem 1rem;
    border: 1.5px solid
        ${({ $hasError }) => ($hasError ? "#ef4444" : "#e2e8f0")};
    border-radius: 12px;
    font-size: 0.925rem;
    color: #0f172a;
    background: white;
    outline: none;
    transition:
        border-color 0.2s,
        box-shadow 0.2s;

    &:focus {
        border-color: ${theme["color-blue"]};
        box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
    }

    &::placeholder {
        color: #94a3b8;
    }

    /* Hide number spinners */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    -moz-appearance: textfield;
`;

const SCChildNameInput = styled.input<{ $hasError?: boolean }>`
    width: 100%;
    padding: 0.7rem 1rem;
    border: 1.5px solid
        ${({ $hasError }) => ($hasError ? "#ef4444" : "#e2e8f0")};
    border-radius: 12px;
    font-size: 0.925rem;
    color: #0f172a;
    background: white;
    outline: none;
    transition:
        border-color 0.2s,
        box-shadow 0.2s;

    &:focus {
        border-color: ${theme["color-blue"]};
        box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
    }

    &::placeholder {
        color: #94a3b8;
    }
`;

const SCChildRemoveBtn = styled.button`
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    background: none;
    border: none;
    font-size: 1.1rem;
    color: #94a3b8;
    cursor: pointer;
    line-height: 1;
    padding: 0.2rem;

    &:hover {
        color: #ef4444;
    }
`;

const SCAddChildBtn = styled.button`
    background: none;
    border: 1.5px dashed #cbd5e1;
    border-radius: 12px;
    padding: 0.7rem 1rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: ${theme["color-blue"]};
    cursor: pointer;
    width: 100%;
    transition: all 0.15s;
    text-align: left;
    margin-bottom: 0.5rem;

    &:hover {
        border-color: ${theme["color-blue"]};
        background: #e0f2fe;
    }
`;

const SCFormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-bottom: 0.75rem;

    label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #334155;

        .required {
            color: #f97316;
        }
        .optional {
            color: #94a3b8;
            font-weight: 400;
            font-size: 0.8rem;
        }
    }

    input[type="text"],
    input[type="tel"],
    input[type="email"] {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1.5px solid #e2e8f0;
        border-radius: 12px;
        font-size: 0.925rem;
        color: #0f172a;
        background: white;
        outline: none;
        transition:
            border-color 0.2s,
            box-shadow 0.2s;

        &:focus {
            border-color: ${theme["color-blue"]};
            box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
        }

        &::placeholder {
            color: #94a3b8;
        }

        &[aria-invalid="true"] {
            border-color: #ef4444;
        }
    }
`;

const SCPhoneInputWrap = styled.div<{ $hasError?: boolean }>`
    display: flex;
    border: 1.5px solid
        ${({ $hasError }) => ($hasError ? "#ef4444" : "#e2e8f0")};
    border-radius: 12px;
    overflow: hidden;
    transition:
        border-color 0.2s,
        box-shadow 0.2s;

    &:focus-within {
        border-color: ${theme["color-blue"]};
        box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
    }

    .phone-prefix {
        display: flex;
        align-items: center;
        padding: 0 0.9rem;
        background: #f1f5f9;
        color: #334155;
        font-weight: 600;
        font-size: 0.925rem;
        border-right: 1px solid #e2e8f0;
        white-space: nowrap;
    }

    select {
        padding: 0.75rem 0.75rem;
        background: #f1f5f9;
        border: none;
        border-right: 1px solid #e2e8f0;
        font-size: 0.9rem;
        font-weight: 600;
        color: #334155;
        outline: none;
        cursor: pointer;
    }

    input {
        flex: 1;
        padding: 0.75rem 1rem;
        border: none;
        font-size: 0.925rem;
        color: #0f172a;
        outline: none;
        background: white;

        &::placeholder {
            color: #94a3b8;
        }
    }
`;

const SCFieldError = styled.div`
    color: #ef4444;
    font-size: 0.8rem;
    margin-top: 2px;
`;

const SCHelperText = styled.div`
    color: ${theme["color-blue"]};
    font-size: 0.8rem;
    font-style: italic;
`;

const SCErrorMessage = styled.div`
    background-color: #fef2f2;
    color: #dc2626;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 14px;
    text-align: center;
    border: 1px solid #fecaca;
`;

const SCSubmitButton = styled(SCButton)`
    background-color: ${theme["color-blue"]};
    color: ${theme["color-white"]};
    padding: 14px 32px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    width: 100%;
    margin-top: 8px;
    transition:
        background-color 0.2s,
        transform 0.1s;
    box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35);

    &:hover:not(:disabled) {
        background-color: ${theme["color-dark-blue"]};
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(14, 165, 233, 0.4);
    }

    &:disabled {
        background-color: #94a3b8;
        cursor: not-allowed;
        box-shadow: none;
        transform: none;
    }
`;

const SCFormFooter = styled.p`
    font-size: 0.8rem;
    color: #94a3b8;
    text-align: center;
    margin-top: 0.75rem;
    line-height: 1.5;
`;

const SCStatsSection = styled.div`
    display: flex;
    justify-content: space-around;
    margin-top: 48px;
    padding: 32px 0;
    gap: 24px;

    @media (max-width: 768px) {
        display: none;
    }
`;

const SCStatItem = styled.div`
    text-align: center;
    flex: 1;

    .stat-value {
        font-size: 24px;
        font-weight: bold;
        color: ${theme["color-blue"]};
        margin-bottom: 8px;
        font-family: "Brockmann", sans-serif;
    }

    .stat-label {
        font-size: 12px;
        color: ${theme["color-grey"]};
    }
`;
